import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  IconFlame,
  IconCalculator,
  IconCheck,
  IconRotate,
  IconAlertCircle,
  IconInfoCircle,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { recordConsumption } from "../../controllers/item_issue.controller";
import toast from "react-hot-toast";

const REASONS = [
  { value: "WASTAGE", label: "Wastage / Spoilage" },
  { value: "BREAKAGE", label: "Accidental Breakage" },
  { value: "STAFF_MEAL", label: "Staff Meal / Consumption" },
  { value: "CLEANING", label: "Cleaning / Operational Use" },
  { value: "BAR_TASTING", label: "Bar Tasting / Quality Check" },
  { value: "INTERNAL_USE", label: "Other Internal Use" },
];

export default function RecordConsumptionView({
  register,
  eligibleItems,
  users,
  prefilledRow,
  onConsumptionSuccess,
}) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [reasonType, setReasonType] = useState(REASONS[0]);
  const [approvedBy, setApprovedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Custodians with active balances
  const activeCustodians = useMemo(() => {
    const map = new Map();
    (register || []).forEach((r) => {
      const remaining = parseFloat(r.remaining_base_quantity) || 0;
      const isClosed =
        r.status === "closed" ||
        r.status === "returned" ||
        r.status === "reconciled" ||
        remaining <= 0.0001;
      if (!isClosed && remaining > 0.0001) {
        if (!map.has(r.user_id)) {
          map.set(r.user_id, {
            value: r.user_id,
            label: `${r.employee_name || r.user_id} (${r.department || "Staff"})`,
          });
        }
      }
    });
    return Array.from(map.values());
  }, [register]);

  // Handle prefill from Custody Register
  useEffect(() => {
    if (prefilledRow) {
      const uOpt = activeCustodians.find((c) => c.value === prefilledRow.user_id) || {
        value: prefilledRow.user_id,
        label: `${prefilledRow.employee_name || prefilledRow.user_id} (${prefilledRow.department || "Staff"})`,
      };
      setSelectedUser(uOpt);

      const matchingItem = (eligibleItems || []).find(
        (i) => i.id === prefilledRow.inventory_item_id
      );
      if (matchingItem) {
        setSelectedItem({
          value: matchingItem.id,
          label: `${matchingItem.title} (Custody: ${prefilledRow.remaining_base_quantity} ${matchingItem.unit})`,
          item: matchingItem,
          custodyRecord: prefilledRow,
        });
      }
    }
  }, [prefilledRow, activeCustodians, eligibleItems]);

  // Products held by selected custodian
  const heldItemOptions = useMemo(() => {
    if (!selectedUser) return [];
    const held = (register || []).filter((r) => {
      const remaining = parseFloat(r.remaining_base_quantity) || 0;
      const isClosed =
        r.status === "closed" ||
        r.status === "returned" ||
        r.status === "reconciled" ||
        remaining <= 0.0001;
      return r.user_id === selectedUser.value && !isClosed && remaining > 0.0001;
    });

    return held.map((r) => {
      const matchingMaster = (eligibleItems || []).find((i) => i.id === r.inventory_item_id);
      return {
        value: r.inventory_item_id,
        label: `${r.item_title} — Custody Rem: ${r.remaining_base_quantity} ${r.unit}`,
        item: matchingMaster || { id: r.inventory_item_id, title: r.item_title, unit: r.unit, alternate_units: [] },
        custodyRecord: r,
      };
    });
  }, [selectedUser, register, eligibleItems]);

  // Units
  const unitOptions = useMemo(() => {
    if (!selectedItem) return [];
    const item = selectedItem.item;
    const list = [
      {
        value: `base_${item.unit}`,
        label: `${item.unit} (Base Stock Unit - 1.0x)`,
        unit_name: item.unit,
        purchase_unit_id: null,
        conversion_factor: 1.0,
      },
    ];

    if (Array.isArray(item.alternate_units)) {
      item.alternate_units.forEach((u) => {
        list.push({
          value: `alt_${u.purchase_unit_id}`,
          label: `${u.unit_name} (${u.conversion_factor} ${item.unit} per ${u.unit_name})`,
          unit_name: u.unit_name,
          purchase_unit_id: u.purchase_unit_id,
          conversion_factor: parseFloat(u.conversion_factor) || 1.0,
        });
      });
    }

    return list;
  }, [selectedItem]);

  const handleItemSelect = (opt) => {
    setSelectedItem(opt);
    if (opt && opt.item) {
      setSelectedUnit({
        value: `base_${opt.item.unit}`,
        label: `${opt.item.unit} (Base Stock Unit - 1.0x)`,
        unit_name: opt.item.unit,
        purchase_unit_id: null,
        conversion_factor: 1.0,
      });
    } else {
      setSelectedUnit(null);
    }
  };

  // Calculations
  const parsedQty = parseFloat(quantity) || 0;
  const factor = selectedUnit ? selectedUnit.conversion_factor : 1.0;
  const consumedBaseQty = parsedQty * factor;
  const custodyRem = selectedItem?.custodyRecord
    ? parseFloat(selectedItem.custodyRecord.remaining_base_quantity) || 0
    : 0;
  const isOverCustody = consumedBaseQty > custodyRem;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      toast.error("Please select a custodian.");
      return;
    }
    if (!selectedItem) {
      toast.error("Please select a product.");
      return;
    }
    if (!selectedUnit) {
      toast.error("Please select a unit.");
      return;
    }
    if (parsedQty <= 0) {
      toast.error("Quantity must be greater than zero.");
      return;
    }
    if (isOverCustody) {
      toast.error(
        `Cannot record consumption of ${consumedBaseQty} base units. Custody balance is only ${custodyRem}!`
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        issue_id: selectedItem.custodyRecord?.id || null,
        user_id: selectedUser.value,
        employee_name: selectedItem.custodyRecord?.employee_name || selectedUser.label,
        department: selectedItem.custodyRecord?.department || null,
        inventory_item_id: selectedItem.value,
        quantity: parsedQty,
        purchase_unit_id: selectedUnit.purchase_unit_id,
        unit_name: selectedUnit.unit_name,
        reason: reasonType.value,
        approved_by: approvedBy,
        notes: notes,
      };

      const res = await recordConsumption(payload);
      if (res.data?.success) {
        toast.success(
          `Wastage / internal usage recorded! (${consumedBaseQty} ${selectedItem.item.unit} recorded in staff custody wastage)`
        );
        setQuantity("");
        setNotes("");
        setApprovedBy("");
        if (onConsumptionSuccess) onConsumptionSuccess();
      } else {
        toast.error(res.data?.message || "Failed to record consumption.");
      }
    } catch (err) {
      console.error("Consumption error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to record consumption.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-base-100 p-6 rounded-2xl border border-base-200 shadow-sm max-w-4xl mx-auto">
      <div className="border-b border-base-200 pb-4 mb-6">
        <h2 className="text-xl font-bold text-base-content flex items-center gap-2">
          <IconFlame size={22} stroke={iconStroke} className="text-warning" />
          Record Non-Sale Internal Usage & Wastage
        </h2>
        <p className="text-xs text-base-content/60 mt-1">
          Account for broken, expired, spoiled, or internally consumed stock. Records custody wastage for informational tracking without modifying warehouse inventory or POS.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Custodian & Product */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Custodian / Staff <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={activeCustodians}
              value={selectedUser}
              onChange={(opt) => {
                setSelectedUser(opt);
                setSelectedItem(null);
                setSelectedUnit(null);
              }}
              placeholder="Select staff with active stock..."
              isClearable
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Product in Custody <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={heldItemOptions}
              value={selectedItem}
              onChange={handleItemSelect}
              isDisabled={!selectedUser}
              placeholder={selectedUser ? "Select item..." : "Choose custodian first"}
            />
          </div>
        </div>

        {/* Quantity, Unit & Reason Type */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Unit <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={unitOptions}
              value={selectedUnit}
              onChange={setSelectedUnit}
              isDisabled={!selectedItem}
              placeholder={selectedItem ? "Choose unit..." : "Select product first"}
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Quantity Used / Lost <span className="text-error">*</span>
            </label>
            <input
              type="number"
              step="any"
              min="0.001"
              required
              className={`input input-sm input-bordered w-full text-sm ${
                isOverCustody ? "input-error" : ""
              }`}
              placeholder="e.g. 1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Usage Category <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={REASONS}
              value={reasonType}
              onChange={setReasonType}
            />
          </div>
        </div>

        {/* Preview Card */}
        {selectedItem && selectedUnit && parsedQty > 0 && (
          <div
            className={`p-4 rounded-xl border ${
              isOverCustody
                ? "bg-error/5 border-error/30 text-error"
                : "bg-warning/5 border-warning/20 text-base-content"
            }`}
          >
            <div className="flex items-center gap-2 mb-2 font-bold text-sm">
              <IconCalculator size={18} stroke={iconStroke} />
              Consumption Impact Preview
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-base-content/60 block">Entered Qty</span>
                <span className="font-bold text-sm">
                  {parsedQty} {selectedUnit.unit_name}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Base Conversion</span>
                <span className="font-bold text-sm">
                  {factor} {selectedItem.item.unit} / {selectedUnit.unit_name}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Deducted Base Qty</span>
                <span className="font-bold text-sm text-warning">
                  {consumedBaseQty.toLocaleString(undefined, { maximumFractionDigits: 3 })}{" "}
                  {selectedItem.item.unit}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Custodian Balance</span>
                <span className="font-bold text-sm text-primary">
                  {custodyRem.toLocaleString()} {selectedItem.item.unit}
                </span>
              </div>
            </div>

            {isOverCustody && (
              <div className="mt-3 flex items-center gap-1.5 text-xs text-error font-semibold">
                <IconAlertCircle size={16} stroke={iconStroke} />
                Quantity exceeds outstanding custody by{" "}
                {(consumedBaseQty - custodyRem).toFixed(2)} {selectedItem.item.unit}!
              </div>
            )}
            {!isOverCustody && (
              <div className="mt-2 text-[11px] text-base-content/60 flex items-center gap-1">
                <IconInfoCircle size={14} stroke={iconStroke} />
                Custody balance will decrease to {(custodyRem - consumedBaseQty).toFixed(2)}{" "}
                {selectedItem.item.unit}. Purely for staff custody tracking (not associated with warehouse stock).
              </div>
            )}
          </div>
        )}

        {/* Approver & Justification */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Supervisor / Approved By
            </label>
            <input
              type="text"
              className="input input-sm input-bordered w-full text-sm"
              value={approvedBy}
              onChange={(e) => setApprovedBy(e.target.value)}
              placeholder="e.g. Head Chef, Duty Manager, F&B Supervisor"
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Reason / Incident Description
            </label>
            <input
              type="text"
              className="input input-sm input-bordered w-full text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Dropped during service, bottle neck chipped"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end pt-4 border-t border-base-200">
          <button
            type="submit"
            disabled={isSubmitting || isOverCustody || parsedQty <= 0 || !selectedItem}
            className="btn btn-warning gap-2 px-8"
          >
            {isSubmitting ? (
              <IconRotate className="animate-spin" size={18} stroke={iconStroke} />
            ) : (
              <IconCheck size={18} stroke={iconStroke} />
            )}
            Record Consumption & Deduct Stock
          </button>
        </div>
      </form>
    </div>
  );
}
